/**
 * onaturelle.js — module registry, utilities, cart helpers
 * --------------------------------------------------------------------------
 * Single global namespace: window.ON
 *
 * Modules register themselves via ON.register('name', initFn).
 * Boot runs every registered module's init() once on DOMContentLoaded.
 *
 * For Shopify Theme Editor reloads, modules that need to rebind after a
 * section reload should listen for 'shopify:section:load' on document
 * inside their init.
 *
 * Conventions and example module: see docs/CONVENTIONS.md.
 */
(function () {
  'use strict';

  const ON = (window.ON = window.ON || {});

  /* ------------------------------------------------------------------------
   * Module registry + boot
   * ------------------------------------------------------------------------ */

  ON.modules = ON.modules || {};

  ON.register = function (name, init) {
    if (typeof init !== 'function') {
      console.error(`[on] register("${name}"): init must be a function`);
      return;
    }
    if (ON.modules[name]) {
      console.warn(`[on] module "${name}" already registered — skipping`);
      return;
    }
    ON.modules[name] = init;
  };

  function boot() {
    Object.entries(ON.modules).forEach(([name, init]) => {
      try {
        init();
      } catch (err) {
        console.error(`[on] module "${name}" failed:`, err);
      }
    });
  }

  // Expose boot for manual re-trigger if external scripts register modules
  // after the initial bootstrap (e.g. Theme Editor section reloads).
  ON.boot = boot;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    // DOM is already parsed (deferred-script path). Defer boot to the next
    // microtask so module registrations later in this same synchronous
    // execution (e.g. ON.register calls further down this file) complete
    // BEFORE boot iterates ON.modules. Without this, boot() runs against
    // an empty module registry and no modules ever initialize.
    Promise.resolve().then(boot);
  }

  /* ------------------------------------------------------------------------
   * DOM utilities
   * ------------------------------------------------------------------------ */

  ON.$  = (sel, root = document) => root.querySelector(sel);
  ON.$$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /**
   * Safe addEventListener with off-handle return.
   *   const off = ON.on(btn, 'click', handler);
   *   off();   // remove listener
   */
  ON.on = function (target, event, handler, options) {
    target.addEventListener(event, handler, options);
    return () => target.removeEventListener(event, handler, options);
  };

  /* ------------------------------------------------------------------------
   * Event bus — custom events on document
   * Naming: 'on:<scope>:<verb>'   e.g. 'on:cart:update', 'on:drawer:open'
   * ------------------------------------------------------------------------ */

  ON.emit = function (name, detail) {
    document.dispatchEvent(new CustomEvent(`on:${name}`, { detail, bubbles: true }));
  };

  ON.listen = function (name, handler) {
    return ON.on(document, `on:${name}`, handler);
  };

  /* ------------------------------------------------------------------------
   * Body-class state — global awareness for drawers, modals, mobile nav
   *   ON.setBodyState('cart', true);   // body.has-cart-open
   *   ON.setBodyState('cart', false);
   * ------------------------------------------------------------------------ */

  ON.setBodyState = function (name, isOpen) {
    document.body.classList.toggle(`has-${name}-open`, !!isOpen);
  };

  /* ------------------------------------------------------------------------
   * Shopify AJAX cart — wrappers
   * Docs: https://shopify.dev/docs/api/ajax/reference/cart
   * Every mutator emits 'on:cart:update' so subscribers can re-render.
   * ------------------------------------------------------------------------ */

  const jsonHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  ON.cart = {
    get() {
      return fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
    },
    add(items) {
      const payload = { items: Array.isArray(items) ? items : [items] };
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      })
        .then((r) => r.json())
        .then((result) => {
          ON.emit('cart:update', { reason: 'add', result });
          return result;
        });
    },
    update(updates) {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'same-origin',
        body: JSON.stringify({ updates }),
      })
        .then((r) => r.json())
        .then((cart) => {
          ON.emit('cart:update', { reason: 'update', cart });
          return cart;
        });
    },
    // Accepts either change({ id, quantity, properties }) OR change(key, quantity).
    // The cart-line stepper/remove handlers use the two-arg form; the PDP buy
    // box uses the object form. Normalising here keeps both working.
    change(changesOrId, quantity) {
      const changes = (changesOrId !== null && typeof changesOrId === 'object')
        ? changesOrId
        : { id: changesOrId, quantity: quantity };
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'same-origin',
        body: JSON.stringify(changes),
      })
        .then((r) => r.json())
        .then((cart) => {
          ON.emit('cart:update', { reason: 'change', cart });
          return cart;
        });
    },
    clear() {
      return fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => {
          ON.emit('cart:update', { reason: 'clear', cart });
          return cart;
        });
    },
  };

  /* ------------------------------------------------------------------------
   * Money formatting — uses Shopify global if present, simple fallback else.
   * ------------------------------------------------------------------------ */

  ON.formatMoney = function (cents, format) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return Shopify.formatMoney(cents, format);
    }
    return `$${(cents / 100).toFixed(2)}`;
  };

  /* ------------------------------------------------------------------------
   * Debounce / rAF throttle — common timing helpers for scroll, resize, input
   * ------------------------------------------------------------------------ */

  ON.debounce = function (fn, wait = 150) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  ON.rafThrottle = function (fn) {
    let queued = false;
    return function (...args) {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fn.apply(this, args);
      });
    };
  };

  /* ------------------------------------------------------------------------
   * Drawer module — generic open/close for any drawer in the DOM
   * ------------------------------------------------------------------------
   * Markup contract:
   *   <aside data-on-drawer="<id>" data-state="closed" aria-hidden="true">…</aside>
   *   <div   data-on-drawer-backdrop="<id>" data-state="closed"></div>
   *   <button data-on-drawer-open="<id>">Open</button>     (anywhere)
   *   <button data-on-drawer-close>Close</button>           (inside the drawer)
   *
   * Behaviors:
   *   - Opening: flips data-state to "open" on drawer + matching backdrop,
   *     sets aria-hidden="false", toggles body.has-<id>-open, emits on:drawer:open.
   *   - Closing: reverse. Triggered by close button click, backdrop click,
   *     or Escape key on any open drawer.
   *   - Multiple drawers can coexist in the DOM; only one open at a time
   *     is the expected pattern (opening drawer A while B is open will leave
   *     B technically open — caller's responsibility to close it first if needed).
   *
   * Idempotent: safe to call init() twice. Triggers are wired via delegation
   * so newly-inserted DOM (e.g. Shopify Theme Editor section reload) just works
   * without rebinding.
   * ---------------------------------------------------------------------- */

  ON.register('drawer', function () {
    function getDrawer(id) {
      return ON.$(`[data-on-drawer="${id}"]`);
    }
    function getBackdrop(id) {
      return ON.$(`[data-on-drawer-backdrop="${id}"]`);
    }

    ON.openDrawer = function (id) {
      const drawer = getDrawer(id);
      if (!drawer) return;
      drawer.dataset.state = 'open';
      drawer.setAttribute('aria-hidden', 'false');

      const backdrop = getBackdrop(id);
      if (backdrop) {
        backdrop.dataset.state = 'open';
        backdrop.setAttribute('aria-hidden', 'false');
      }

      ON.setBodyState(id, true);
      ON.emit('drawer:open', { id });

      // Move focus to the first focusable element inside the drawer
      const focusable = drawer.querySelector(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) {
        // Defer to next frame so the slide-in animation doesn't get jumped
        requestAnimationFrame(() => focusable.focus({ preventScroll: true }));
      }
    };

    ON.closeDrawer = function (id) {
      const drawer = getDrawer(id);
      if (!drawer) return;
      drawer.dataset.state = 'closed';
      drawer.setAttribute('aria-hidden', 'true');

      const backdrop = getBackdrop(id);
      if (backdrop) {
        backdrop.dataset.state = 'closed';
        backdrop.setAttribute('aria-hidden', 'true');
      }

      ON.setBodyState(id, false);
      ON.emit('drawer:close', { id });
    };

    // Delegated click handler — catches openers, closers, backdrops everywhere.
    ON.on(document, 'click', function (e) {
      const opener = e.target.closest('[data-on-drawer-open]');
      if (opener) {
        e.preventDefault();
        ON.openDrawer(opener.dataset.onDrawerOpen);
        return;
      }
      const closer = e.target.closest('[data-on-drawer-close]');
      if (closer) {
        const drawer = closer.closest('[data-on-drawer]');
        if (drawer) ON.closeDrawer(drawer.dataset.onDrawer);
        return;
      }
      const backdrop = e.target.closest('[data-on-drawer-backdrop]');
      if (backdrop && e.target === backdrop) {
        ON.closeDrawer(backdrop.dataset.onDrawerBackdrop);
        return;
      }
    });

    // ESC closes whichever drawer is currently open
    ON.on(document, 'keydown', function (e) {
      if (e.key !== 'Escape') return;
      const openDrawer = ON.$('[data-on-drawer][data-state="open"]');
      if (openDrawer) {
        ON.closeDrawer(openDrawer.dataset.onDrawer);
      }
    });
  });

  /* ------------------------------------------------------------------------
   * Cart module — wires the cart drawer to live Shopify cart data
   * ------------------------------------------------------------------------
   * Responsibilities:
   *   - Re-render the drawer body from the Shopify Section Rendering API
   *     on every `cart:update` event and on `drawer:open` for the cart.
   *   - Wire stepper +/- and remove buttons (delegated handlers, so freshly
   *     re-rendered DOM works without rebinding).
   *   - Update the cart count badges throughout the chrome (the spans
   *     with [data-cart-count] in the header, mobile nav, and cart head).
   *
   * NOT responsible (deliberately, for now):
   *   - Intercepting product form submissions on Dawn-driven pages. Those
   *     still use Dawn's flow. The drawer simply refreshes on next open.
   *     New PDPs (chat 4+) will call ON.cart.add() directly, which emits
   *     cart:update and auto-opens this drawer.
   *   - Tier-aware upsell messaging ("Add 1 more for 15% off"). That's
   *     chat 10 territory.
   * ---------------------------------------------------------------------- */

  ON.register('cart', function () {
    // The Section Rendering API keys responses by the INSTANCE ID (e.g.
    // sections--21562453__cart-drawer), not the section type name. Requesting
    // by type produced HTML rendered with schema defaults rather than the
    // header-group settings — image_picker settings (gift_bag_image,
    // gift_key_image) have no defaults, so the gift-row <img>s came back
    // empty after every add-to-cart. Read the instance ID off the rendered
    // DOM at init and only fall back to the type name if the drawer isn't
    // on-page yet (e.g. section not registered).
    // History: first version used 'cart-drawer' which silently matched a
    // leftover Dawn section from the duplicated theme. Second version used
    // 'naturelle-cart-drawer' — worked for text settings (which have schema
    // defaults) but broke image_picker rendering. Third version reads the
    // real instance ID; do not regress this a fourth time.
    const cartSectionEl = document.querySelector('.naturelle-cart')?.closest('[id^="shopify-section-"]');
    const SECTION_ID = cartSectionEl ? cartSectionEl.id.replace(/^shopify-section-/, '') : 'naturelle-cart-drawer';

    let refreshInflight = null;

    function refresh() {
      // Coalesce simultaneous refresh requests
      if (refreshInflight) return refreshInflight;

      // Cache-bust the section fetch — /?sections= responses can be served
      // from the browser/CDN cache and Shopify clears prefetch/prerender
      // caches on cart writes (Clear-Site-Data), but the section HTML itself
      // can still come back stale without a unique URL.
      const url = `/?sections=${SECTION_ID}&_=${Date.now()}`;

      refreshInflight = fetch(url, {
        headers: { 'Accept': 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin'
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          const html = data[SECTION_ID];
          if (!html) return;

          // Parse response and pluck the inner content. Replace the
          // existing .naturelle-cart__inner innerHTML so the outer
          // <aside>'s data-state / aria-hidden survive (open drawer
          // stays open during refresh).
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          const newInner = tmp.querySelector('.naturelle-cart__inner');
          const oldInner = ON.$('.naturelle-cart__inner');
          if (newInner && oldInner) {
            oldInner.innerHTML = newInner.innerHTML;
          }

          // Sync all cart count badges across the page chrome.
          ON.cart.get().then(function (cart) {
            ON.$$('[data-cart-count]').forEach(function (badge) {
              badge.textContent = cart.item_count;
              if (cart.item_count > 0) {
                badge.removeAttribute('hidden');
              } else {
                badge.setAttribute('hidden', '');
              }
            });
          }).catch(function () { /* swallow — non-fatal */ });
        })
        .catch(function (err) {
          console.error('[on/cart] refresh failed:', err);
        })
        .finally(function () {
          refreshInflight = null;
        });

      return refreshInflight;
    }

    // Public — useful for external scripts that mutate the cart out-of-band
    ON.cart.refreshDrawer = refresh;

    // Auto-refresh on every cart change
    ON.listen('cart:update', refresh);

    // Refresh on drawer open (catches stale state from Dawn add-to-cart flows)
    ON.listen('drawer:open', function (e) {
      if (e && e.id === 'cart') refresh();
    });

    // Delegated click handler for stepper +/- and remove buttons.
    // Works after refresh too since the listener is on document.
    ON.on(document, 'click', function (e) {
      const inc = e.target.closest('[data-cart-line-inc]');
      const dec = e.target.closest('[data-cart-line-dec]');
      const rem = e.target.closest('[data-cart-line-remove]');

      if (inc) {
        e.preventDefault();
        const key = inc.dataset.cartLineInc;
        const qty = parseInt(inc.dataset.cartLineQty, 10) || 1;
        ON.cart.change(key, qty + 1)
          .catch((err) => console.error('[on/cart] inc failed:', err));
        return;
      }
      if (dec) {
        e.preventDefault();
        const key = dec.dataset.cartLineDec;
        const qty = parseInt(dec.dataset.cartLineQty, 10) || 1;
        // Going from 1 to 0 removes the line — Shopify cart API handles this
        ON.cart.change(key, Math.max(0, qty - 1))
          .catch((err) => console.error('[on/cart] dec failed:', err));
        return;
      }
      if (rem) {
        e.preventDefault();
        const key = rem.dataset.cartLineRemove;
        ON.cart.change(key, 0)
          .catch((err) => console.error('[on/cart] remove failed:', err));
        return;
      }

      const upsellAdd = e.target.closest('[data-cart-upsell-add]');
      if (upsellAdd) {
        e.preventDefault();
        const variantId = upsellAdd.dataset.cartUpsellAdd;
        if (!variantId) return;
        upsellAdd.disabled = true;
        ON.cart.add({ id: variantId, quantity: 1 })
          .catch(function (err) {
            console.error('[on/cart] upsell add failed:', err);
          })
          .finally(function () {
            upsellAdd.disabled = false;
          });
        return;
      }
    });
  });
  /* ------------------------------------------------------------------------
   * Predictive search module — debounced input → section AJAX → swap results
   * ------------------------------------------------------------------------
   * Markup contract (rendered by sections/predictive-search.liquid):
   *   <aside data-on-drawer="search">…
   *     <input data-predictive-search-input />
   *     <div data-predictive-search-results>…initial empty state…</div>
   *   …</aside>
   *   <div data-on-drawer-backdrop="search"></div>
   *
   * Behavior:
   *   - Listens for input on [data-predictive-search-input]
   *   - Debounces 200ms, then fetches /search/suggest with the query
   *     plus section_id=predictive-search so the section is rendered
   *     server-side with predictive_search.terms populated.
   *   - AbortController cancels in-flight requests when a newer keystroke
   *     arrives — only the latest response wins.
   *   - Replaces innerHTML of [data-predictive-search-results] with the
   *     swap target extracted from the fetched section HTML.
   *   - In-memory Map caches results by query so retyping is instant.
   *   - Empty input → fetches with q='' → section renders the empty
   *     state (popular searches + featured products) → swap restores it.
   *   - ArrowDown / ArrowUp navigate focus between [data-search-link]
   *     items. Tab works automatically via DOM order.
   *   - Drawer module handles Esc, backdrop click, close button, and
   *     auto-focusing the search input on open.
   *   - On close, clear the input so the next open starts empty.
   * ---------------------------------------------------------------------- */
  ON.register('predictiveSearch', function () {
    const panel = ON.$('[data-on-drawer="search"]');
    if (!panel) return;

    const input = panel.querySelector('[data-predictive-search-input]');
    const results = panel.querySelector('[data-predictive-search-results]');
    if (!input || !results) return;

    const cache = new Map();
    let abortCtrl = null;

    // Preserve the initial empty-state HTML so we can restore it instantly
    // when the input is cleared, without a network round-trip.
    cache.set('', results.innerHTML);

    function buildUrl(q) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const base = root.endsWith('/') ? root : root + '/';
      const params = new URLSearchParams({
        q: q,
        'resources[type]': 'product,query,collection,page',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each',
        section_id: 'predictive-search',
      });
      return base + 'search/suggest?' + params.toString();
    }

    function applyHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('[data-predictive-search-results]');
      if (incoming) {
        results.innerHTML = incoming.innerHTML;
      }
    }

    function fetchResults(q) {
      // Cache hit — apply synchronously, skip fetch
      if (cache.has(q)) {
        results.innerHTML = cache.get(q);
        return;
      }

      // Cancel any in-flight request
      if (abortCtrl) abortCtrl.abort();
      abortCtrl = new AbortController();

      fetch(buildUrl(q), { signal: abortCtrl.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('search/suggest ' + res.status);
          return res.text();
        })
        .then(function (html) {
          // Extract the inner HTML of the results container from the
          // fetched section, then cache and apply.
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incoming = doc.querySelector('[data-predictive-search-results]');
          if (!incoming) return;
          const inner = incoming.innerHTML;
          cache.set(q, inner);
          results.innerHTML = inner;
        })
        .catch(function (err) {
          if (err && err.name !== 'AbortError') {
            console.warn('[on/search] fetch failed:', err);
          }
        })
        .finally(function () {
          abortCtrl = null;
        });
    }

    const onInput = ON.debounce(function (e) {
      fetchResults(e.target.value.trim());
    }, 200);

    ON.on(input, 'input', onInput);

    // ── One-click-to-type focus ─────────────────────────────────────
    // iOS Safari requires programmatic .focus() to run SYNCHRONOUSLY
    // inside the user-initiated click event to preserve the gesture
    // context needed to pop the soft keyboard. The drawer module's
    // own auto-focus is RAF-deferred (too late for iOS) AND its
    // generic "first focusable" querySelector scans DOM order and
    // lands on the form's hidden inputs (`options[prefix]`, `type`)
    // first — .focus() on hidden inputs is a silent no-op, so the
    // search input never receives focus. We target it explicitly
    // here. Fresh querySelector each call so this survives Theme
    // Editor section re-renders.
    ON.on(document, 'click', function (e) {
      if (!e.target.closest('[data-on-drawer-open="search"]')) return;
      const liveInput = document.querySelector('[data-predictive-search-input]');
      if (liveInput) liveInput.focus({ preventScroll: true });
    });

    // Keyboard nav within the panel
    ON.on(panel, 'keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

      const items = Array.from(results.querySelectorAll('[data-search-link]'));
      if (items.length === 0) return;

      e.preventDefault();
      const active = document.activeElement;
      let idx;

      if (active === input) {
        idx = e.key === 'ArrowDown' ? 0 : items.length - 1;
      } else {
        const current = items.indexOf(active);
        if (e.key === 'ArrowDown') {
          idx = current < items.length - 1 ? current + 1 : 0;
        } else {
          idx = current > 0 ? current - 1 : -1;
        }
      }

      if (idx === -1) {
        input.focus();
      } else {
        items[idx].focus({ preventScroll: false });
      }
    });

    // On close, reset the input. Next open starts fresh.
    ON.on(document, 'on:drawer:close', function (e) {
      if (e.detail && e.detail.id === 'search' && input.value) {
        input.value = '';
        results.innerHTML = cache.get('') || results.innerHTML;
      }
    });
  });

})();
